import React, { useContext, useEffect, useState } from 'react';
import DownloadIcon from '@deere/ux.brand-foundations/icons/download.svg';
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    List,
    ListItem,
    SelectField,
    Option,
    FormLabel,
    DateRangePicker,
    InputAdornment,
    TextField,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@deere/fuel-react';
import { DateTime } from 'luxon';
import styles from './ClosedJobBids.module.css';
import { AlertBox } from '../../components/AlertBox.jsx';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import { visuallyHidden } from '@mui/utils';
import { CalendarMonth } from '@mui/icons-material';
import { ASSIGNMENT_TYPE } from '../../constants.js';
import { LoggedInContext } from '../../context/LoggedInContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import { getComparator } from './sortingUtil.js';

const phases = {
    withdrawal: 'In Bid Withdrawal Period',
    ready: 'Closed & Ready To Assign',
    assigned: 'Closed & Assigned',
};

const readyColumns = [
    { id: 'bidNumber', label: 'Job #' },
    { id: 'facility', label: 'Facility' },
    { id: 'department', label: 'Department' },
    { id: 'classification', label: 'Classification' },
    { id: 'noOfOpenings', label: '# Openings' },
    { id: 'jobGrade', label: 'Job Grade' },
    { id: 'withdrawalEndDate', label: 'Bid Withdrawal Close' },
    { id: 'biddersSelected', label: 'Bidders Selected', minWidth: 240 },
];

const ReadyTableHead = ({ order, orderBy, onRequestSort }) => {
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {readyColumns.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align="left"
                        style={{ minWidth: headCell.minWidth }}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                            IconComponent={ArrowDropDownIcon}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc'
                                        ? 'sorted descending'
                                        : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

const getReadyBiddersSelected = (bid) =>
    new Array(bid.noOfOpenings).fill(0).filter((_, i) => {
        const pendingBid = bid.assignmentList.find(
            (a) => a.openingIndex === i + 1
        );
        return pendingBid && !!pendingBid.assignmentType;
    }).length;

const ClosedJobRow = ({ closedJob, jobBiddingPhase }) => {
    const navigate = useNavigate();

    const getAssignedJobsLabel = () => {
        const activeAssignments = closedJob.assignmentList.filter(
            (a) =>
                a.openingIndex !== -1 &&
                a.assignmentType &&
                a.assignmentType !== ASSIGNMENT_TYPE.DELETE
        );

        const assignedBidders = activeAssignments.filter(
            (a) => a.assignmentType === ASSIGNMENT_TYPE.ASSIGNED
        );

        if (assignedBidders.length > 0) {
            return `${assignedBidders.length} of ${closedJob.noOfOpenings} Bidder(s) Selected`;
        }

        if (activeAssignments.length > 0) {
            const firstType = activeAssignments[0].assignmentType;
            if (firstType === ASSIGNMENT_TYPE.NO_QUALIFIED_BIDDER) {
                return 'No Qualified Bidder';
            }
            if (firstType === ASSIGNMENT_TYPE.REVOKED) {
                return 'Job Opening Revoked';
            }
        }

        return `0 of ${closedJob.noOfOpenings} Bidder(s) Selected`;
    };

    return (
        <ListItem
            onClick={() => navigate('/closed-jobs/' + closedJob.bidNumber)}
            className={styles.jobBidRow}
        >
            <Grid container className={styles.jobBidRowInner}>
                <Grid container item xs={12} sm={9.5} md={9}>
                    <Grid item xs={12} className={styles.jobBidRowTitle}>
                        <span>
                            {closedJob.classification} #{closedJob.bidNumber}{' '}
                        </span>
                        <span>- ({closedJob.noOfOpenings}) openings</span>
                    </Grid>
                    <Grid item xs={12} className={styles.jobBidInfoSection}>
                        <span>{closedJob.facility}</span>
                        <span>Dept: {closedJob.department}</span>
                        <span>Job Grade: {closedJob.jobGrade}</span>
                        <span>
                            Bid Withdrawal Close {closedJob.withdrawalDate}
                        </span>
                    </Grid>
                </Grid>
                {jobBiddingPhase === 'assigned' &&
                    closedJob.status !== 'CANCELED' && (
                        <Grid
                            item
                            xs={12}
                            sm={2.5}
                            md={3}
                            className={styles.jobBiddersSection}
                        >
                            <span>{getAssignedJobsLabel()}</span>
                        </Grid>
                    )}
                {closedJob.status === 'CANCELED' && (
                    <Grid
                        item
                        xs={12}
                        sm={2.5}
                        md={3}
                        className={styles.jobBiddersSection}
                    >
                        <span>Cancelled and Marked as Posted in Error</span>
                    </Grid>
                )}
            </Grid>
        </ListItem>
    );
};

export const ClosedJobBids = () => {
    const [closedJobBids, setClosedJobBids] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const jobBiddingPhase = searchParams.get('phase') ?? '';
    const [alertMsg, setAlertMsg] = useState();
    const [alertVariant, setAlertVariant] = useState(false);
    const start = DateTime.now().minus({ months: 3 }).startOf('day').toJSDate();
    const end = DateTime.now().toJSDate();
    const [dateRange, setDateRange] = useState([start, end]);
    const [dateRangevariant, setDateRangevariant] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('facility');
    const { makeRequest } = useContext(LoggedInContext);

    const handleRequestSort = (_event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const phaseHandler = (event) => {
        const phase = event.target.value;
        setSearchTerm('');
        setSearchParams(phase ? { phase } : {}, { replace: true });
    };

    const filteredJobBids = searchTerm.trim()
        ? closedJobBids.filter((job) => {
              const term = searchTerm.trim().toLowerCase();
              return [
                  job.bidNumber,
                  job.facility,
                  job.department,
                  job.classification,
                  job.noOfOpenings,
                  job.jobGrade,
                  job.withdrawalDate,
                  job.reqNumber,
                  job.status,
              ].some((field) =>
                  String(field ?? '')
                      .toLowerCase()
                      .includes(term)
              );
          })
        : closedJobBids;

    // Keep the selected phase in the URL so returning to this list (via the
    // Back breadcrumb or browser history) restores the selection and refetches.
    useEffect(() => {
        if (jobBiddingPhase) {
            getClosedBidsByPhase(jobBiddingPhase, dateRange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobBiddingPhase, makeRequest]);

    const getClosedBidsByPhase = async (phase, dates) => {
        const jobsPostedBeforeDate = DateTime.fromJSDate(dates[0]).toISO();
        const jobsPostedAfterDate = DateTime.fromJSDate(dates[1]).toISO();
        setIsLoading(true);
        let response = '';
        const url = `${process.env.REACT_APP_API_ENDPOINT}/bids/closed?phase=${phase}`;
        try {
            if (phase === 'assigned')
                response = await makeRequest(
                    url +
                        '&jobsPostedBeforeDate=' +
                        jobsPostedBeforeDate +
                        '&jobsPostedAfterDate=' +
                        jobsPostedAfterDate
                );
            else {
                response = await makeRequest(url);
            }
            if (response.status === 200) {
                const responseData = await response.json();
                const jobBids = responseData
                    .map((bid) => ({
                        bidNumber: bid.id,
                        facility: bid.facility,
                        department: bid.department,
                        classification: bid.classification.classification,
                        noOfOpenings: bid.noOfOpenings,
                        selectedBidders: bid.assignmentList.filter(
                            (a) => a.openingIndex !== -1
                        ).length,
                        assignmentList: bid.assignmentList,
                        jobGrade: bid.jobGrade,
                        endDate: DateTime.fromISO(bid.endDate).toFormat(
                            'LL/dd/yy'
                        ),
                        withdrawalDate: DateTime.fromISO(
                            bid.withdrawalEndDate
                        ).toLocaleString(DateTime.DATE_SHORT),
                        withdrawalEndDate: bid.withdrawalEndDate,
                        reqNumber: bid.reqNumber,
                        status: bid.status.trimEnd(),
                        biddersSelected: getReadyBiddersSelected(bid),
                    }))
                    .sort(comparator);
                setClosedJobBids(jobBids);
            } else {
                setAlertMsg(
                    errorBannerFromResponse(
                        response,
                        'We are unable to retrieve closed jobs. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            }
            setIsLoading(false);
        } catch (err) {
            setAlertMsg(
                errorBannerFromResponse(
                    err,
                    'We are unable to retrieve closed jobs. Please try again later.'
                ).message
            );
            setAlertVariant('error');
            setIsLoading(false);
        }
    };

    const comparator = (a, b) => {
        const facilityA = a.facility.toUpperCase(); // ignore upper and lowercase
        const facilityB = b.facility.toUpperCase(); // ignore upper and lowercase
        if (facilityA < facilityB) {
            return -1;
        }
        if (facilityA > facilityB) {
            return 1;
        }
        if (facilityA === facilityB) {
            const departmentA = a.department.toUpperCase(); // ignore upper and lowercase
            const departmentB = b.department.toUpperCase(); // ignore upper and lowercase
            if (departmentA < departmentB) {
                return -1;
            }
            if (departmentA > departmentB) {
                return 1;
            }
        }
        return 0;
    };

    async function excelDownload(response) {
        const filename = response.headers
            .get('content-disposition')
            .split('filename=')[1];
        const closedJobDownloadResponse = await response.blob();
        const fileURL = window.URL.createObjectURL(closedJobDownloadResponse);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = filename;
        link.target = '_blank';
        link.click();
        window.URL.revokeObjectURL(fileURL);
        link.remove();
    }

    const closedAssignedBidderListDownload = async () => {
        const jobsPostedBeforeDate = DateTime.fromJSDate(dateRange[0]).toISO();
        const jobsPostedAfterDate = DateTime.fromJSDate(dateRange[1]).toISO();
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/closedAssignedJobsDownload?` +
                'jobsPostedBeforeDate=' +
                jobsPostedBeforeDate +
                '&jobsPostedAfterDate=' +
                jobsPostedAfterDate
        );

        if (response.ok) {
            await excelDownload(response);
        } else {
            setAlertMsg(
                errorBannerFromResponse(
                    response,
                    'Due to technical issues, we are unable to download closed and assigned jobs. Please try again later.'
                ).message
            );
            setAlertVariant('error');
        }
    };

    const activeBidderListDownload = async () => {
        const endpoint =
            jobBiddingPhase === 'ready'
                ? '/closedJobsDownload'
                : '/withdrawalJobsDownload';
        const response = await makeRequest(
            process.env.REACT_APP_API_ENDPOINT + endpoint
        );

        if (response.ok) {
            await excelDownload(response);
        } else {
            setAlertMsg(
                errorBannerFromResponse(
                    response,
                    'Due to technical issues, we are unable to download closed jobs. Please try again later.'
                ).message
            );
            setAlertVariant('error');
        }
    };
    const dateRangeHandler = async (dates) => {
        if (!dates[1]) {
            return;
        } else {
            setDateRange(dates);
            setDateRangevariant(true);
            setIsLoading(true);
            await getClosedBidsByPhase(jobBiddingPhase, dates);
        }
    };
    return (
        <div className={styles.parentContainer}>
            <Box className={styles.contentToContainer}>
                <Box
                    className={styles.headerContainer}
                    display="block"
                    bgcolor="background.default"
                >
                    {alertVariant && (
                        <AlertBox msg={alertMsg} variant={alertVariant} />
                    )}
                    <Typography variant="h1" className={styles.heading}>
                        Closed Job Listings
                    </Typography>
                    Choose a Job Bidding Phase to view job bids. Click on a job
                    bid number to view job details.
                </Box>
                <Box style={{ marginBottom: '1.5rem' }}>
                    <span style={{ color: '#c21020' }}>* </span>
                    Required fields.
                </Box>
                <Box style={{ marginBottom: '2rem' }}>
                    <Grid
                        style={{
                            justifyContent: 'space-between',
                            alignItem: 'end',
                        }}
                        container
                    >
                        <Grid item xs={12} sm={4}>
                            <SelectField
                                value={jobBiddingPhase}
                                onChange={phaseHandler}
                                required={true}
                                label="Job Bidding Phase"
                            >
                                <Option disabled hidden value="">
                                    Choose Phase
                                </Option>
                                {Object.keys(phases).map((phase, index) => (
                                    <Option key={index} value={phase}>
                                        {phases[phase]}
                                    </Option>
                                ))}
                            </SelectField>
                        </Grid>
                        {jobBiddingPhase === 'assigned' && (
                            <>
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    style={{ paddingLeft: '2rem' }}
                                    className={styles.dateRangeStyle}
                                >
                                    <FormLabel required={true}>
                                        * Month and Year
                                    </FormLabel>
                                    <DateRangePicker
                                        size="small"
                                        value={dateRange}
                                        minDate={
                                            new Date('August 1, 2023 12:00:00')
                                        }
                                        onChange={(dates) => {
                                            dateRangeHandler(dates);
                                        }}
                                        startDatePlaceholder="Select Start Date"
                                        endDatePlaceholder="Select End Date"
                                        StartInputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CalendarMonth />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    className={styles.buttonStyle}
                                >
                                    <Button
                                        className={styles.buttonDownload}
                                        variant="primary"
                                        disabled={
                                            closedJobBids.length === 0 ||
                                            isLoading
                                        }
                                        startIcon={
                                            <img alt=">>" src={DownloadIcon} />
                                        }
                                        onClick={
                                            closedAssignedBidderListDownload
                                        }
                                    >
                                        Download Assignments for All Jobs
                                    </Button>
                                </Grid>
                            </>
                        )}
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            className={styles.buttonStyle}
                        >
                            {(jobBiddingPhase === 'ready' ||
                                jobBiddingPhase === 'withdrawal') && (
                                <Button
                                    className={styles.buttonDownload}
                                    variant="primary"
                                    disabled={
                                        closedJobBids.length === 0 || isLoading
                                    }
                                    startIcon={
                                        <img alt=">>" src={DownloadIcon} />
                                    }
                                    onClick={activeBidderListDownload}
                                >
                                    Download Active Bidders List for All Jobs
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                {isLoading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!isLoading &&
                    jobBiddingPhase !== '' &&
                    jobBiddingPhase !== 'assigned' &&
                    alertVariant === false &&
                    closedJobBids.length === 0 && (
                        <Card>
                            <CardContent>
                                <Typography gutterBottom variant="h4">
                                    You do not have any job bids in the{' '}
                                    {phases[jobBiddingPhase]} phase at this
                                    time.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                {!isLoading &&
                    jobBiddingPhase === 'assigned' &&
                    alertVariant === false &&
                    closedJobBids.length === 0 &&
                    dateRangevariant && (
                        <Card>
                            <CardContent>
                                <Typography gutterBottom variant="h4">
                                    You do not have any job bids in the{' '}
                                    {phases[jobBiddingPhase]} phase for the date
                                    you selected.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                {!isLoading &&
                    jobBiddingPhase === 'ready' &&
                    closedJobBids.length > 0 && (
                        <Box
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginBottom: '1rem',
                            }}
                        >
                            <TextField
                                id="closed-job-search"
                                type="text"
                                size="small"
                                placeholder="Search by job number, facility, department, classification"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                fullWidth
                                sx={{ width: '30rem' }}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                            />
                        </Box>
                    )}
                {!isLoading &&
                    jobBiddingPhase === 'ready' &&
                    closedJobBids.length > 0 && (
                        <Paper>
                            <TableContainer>
                                <Table
                                    size="small"
                                    sx={{
                                        '& tbody td, & thead th': {
                                            paddingTop: '12px',
                                            paddingBottom: '12px',
                                        },
                                    }}
                                >
                                    <ReadyTableHead
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                    />
                                    <TableBody>
                                        {[...filteredJobBids]
                                            .sort(getComparator(order, orderBy))
                                            .map((closedJob) => (
                                                <TableRow
                                                    hover
                                                    tabIndex={-1}
                                                    key={closedJob.bidNumber}
                                                >
                                                    <TableCell>
                                                        <Link
                                                            to={
                                                                '/closed-jobs/' +
                                                                closedJob.bidNumber
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {
                                                                closedJob.bidNumber
                                                            }
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        {closedJob.facility}
                                                    </TableCell>
                                                    <TableCell>
                                                        {closedJob.department}
                                                    </TableCell>
                                                    <TableCell>
                                                        {
                                                            closedJob.classification
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {closedJob.noOfOpenings}
                                                    </TableCell>
                                                    <TableCell>
                                                        {closedJob.jobGrade}
                                                    </TableCell>
                                                    <TableCell>
                                                        {
                                                            closedJob.withdrawalDate
                                                        }
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            minWidth: 240,
                                                            whiteSpace:
                                                                'nowrap',
                                                        }}
                                                    >
                                                        <Typography
                                                            className={
                                                                styles.successfulBidStatus
                                                            }
                                                            variant="body1"
                                                        >
                                                            <WarningAmberIcon
                                                                className={
                                                                    styles.alertIcon
                                                                }
                                                            />
                                                            {`${closedJob.biddersSelected} of ${closedJob.noOfOpenings} Bidder(s) Selected`}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {filteredJobBids.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        readyColumns.length
                                                    }
                                                >
                                                    <Typography variant="body1">
                                                        No jobs match your
                                                        search.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                {!isLoading &&
                    jobBiddingPhase !== '' &&
                    jobBiddingPhase !== 'ready' &&
                    closedJobBids.length > 0 && (
                        <>
                            <Card>
                                <List>
                                    {closedJobBids.map((closedJob, index) => (
                                        <ClosedJobRow
                                            closedJob={closedJob}
                                            jobBiddingPhase={jobBiddingPhase}
                                            key={index}
                                        />
                                    ))}
                                </List>
                            </Card>
                        </>
                    )}
            </Box>
        </div>
    );
};
